// Turning page meta into HTML tags, and putting them into the app's index.html.
// The Cloudflare Worker (worker/index.ts) does this before the page is sent, so
// search engines and link previews that don't run scripts still see each
// page's own title, description, canonical address, social tags and structured
// data. Every injected tag carries data-seo so the app can swap them as people
// move between pages.

import { type SeoContext, type SeoMeta, seoLinks, socialTags } from './meta';

export const escapeAttr = (s: string) => s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const escapeText = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** JSON for a <script>: "<" is escaped so text from users can never close the script tag. */
export const jsonForScript = (data: unknown) => JSON.stringify(data).replace(/</g, '\\u003c').replace(/>/g, '\\u003e').replace(/&/g, '\\u0026').replace(/\u2028/g, '\\u2028').replace(/\u2029/g, '\\u2029');

export const ROBOTS_INDEX = 'index, follow, max-image-preview:large, max-snippet:-1';
export const ROBOTS_NOINDEX = 'noindex, nofollow';

/** The tags that go in <head>, without the <title>. */
export function headTags(ctx: SeoContext, meta: SeoMeta): string[] {
  const tags: string[] = [
    `<meta name="description" content="${escapeAttr(meta.description)}" data-seo>`,
    `<meta name="robots" content="${meta.index ? ROBOTS_INDEX : ROBOTS_NOINDEX}" data-seo>`,
  ];
  for (const l of seoLinks(ctx, meta)) tags.push(`<link rel="${l.rel}"${l.hreflang ? ` hreflang="${l.hreflang}"` : ''} href="${escapeAttr(l.href)}" data-seo>`);
  // Social tags only for pages meant to be shared.
  if (meta.index) {
    for (const t of socialTags(ctx, meta)) tags.push(`<meta ${t.property ? `property="${t.property}"` : `name="${t.name}"`} content="${escapeAttr(t.content)}" data-seo>`);
  }
  for (const data of meta.jsonLd) tags.push(`<script type="application/ld+json" data-seo>${jsonForScript(data)}</script>`);
  return tags;
}

/** Plain HTML of the page's main content, for crawlers that don't run scripts. Shown only when scripts are off. */
export function noscriptHtml(meta: SeoMeta): string {
  if (!meta.noscript) return '<noscript data-seo>TradePrep needs JavaScript to run.</noscript>';
  const { heading, text, links } = meta.noscript;
  return `<noscript data-seo><main><h1>${escapeText(heading)}</h1><p>${escapeText(text)}</p><ul>${links
    .map((l) => `<li><a href="${escapeAttr(l.href)}">${escapeText(l.label)}</a></li>`)
    .join('')}</ul></main></noscript>`;
}

/** Remove what a page already says about itself, so each tag appears once. */
const STRIP = [
  /<title>[\s\S]*?<\/title>\s*/gi,
  /<meta\s+name=["'](?:description|robots)["'][^>]*>\s*/gi,
  /<meta\s+(?:property|name)=["'](?:og|twitter):[^"']*["'][^>]*>\s*/gi,
  /<link\s+rel=["'](?:canonical|alternate)["'][^>]*>\s*/gi,
  /<script\s+type=["']application\/ld\+json["'][\s\S]*?<\/script>\s*/gi,
  /<noscript>[\s\S]*?<\/noscript>\s*/gi,
];

/** index.html with this page's own tags and a plain-HTML fallback of its content. */
export function injectSeo(html: string, ctx: SeoContext, meta: SeoMeta): string {
  let out = html;
  for (const re of STRIP) out = out.replace(re, '');
  const head = [`<title>${escapeText(meta.title)}</title>`, ...headTags(ctx, meta)].join('\n    ');
  out = out.replace('</head>', `    ${head}\n  </head>`);
  // The fallback sits right after the app's mount point; the app replaces nothing in it.
  out = out.replace(/(<div id="root"><\/div>)/, `$1\n    ${noscriptHtml(meta)}`);
  return out;
}
